// src/services/EnhancedAthleteService.js
import { db, auth } from '../firebase'; 
import { 
  doc, setDoc, getDoc, updateDoc, collection, addDoc, 
  query, where, getDocs, deleteDoc, serverTimestamp 
} from 'firebase/firestore';

/**
 * Obtiene el perfil del usuario actual con su tipo (atleta/entrenador)
 */
export const getCurrentUserProfile = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return null;

    const userDoc = await getDoc(doc(db, 'users', user.uid));
    if (!userDoc.exists()) return null;

    return {
      uid: user.uid,
      email: user.email,
      ...userDoc.data()
    };
  } catch (error) {
    console.error('Error obteniendo perfil:', error);
    return null;
  }
};

/**
 * Vincula un atleta directamente a su propia cuenta
 * Solo funciona si el usuario es tipo 'atleta' o no tiene tipo definido
 */
export const linkAthleteToOwnAccount = async (athleteId, competitionId, athleteData) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('AUTHENTICATION_REQUIRED');
    }

    const userProfile = await getCurrentUserProfile();
    
    // Verificar si ya tiene un atleta vinculado
    if (userProfile?.linkedAthlete) {
      throw new Error('ATHLETE_ALREADY_LINKED');
    }

    // Si es entrenador, no puede vincularse como atleta
    if (userProfile?.userType === 'coach') {
      throw new Error('COACH_CANNOT_LINK_AS_ATHLETE');
    }

    // Crear la vinculación
    const linkData = {
      linkedAthlete: {
        athleteId,
        competitionId,
        athleteName: athleteData.fullName,
        athleteTeam: athleteData.affiliation,
        athleteNumber: athleteData.idNumber,
        linkedAt: serverTimestamp(),
        status: 'confirmed' // Vinculación directa, no necesita aprobación
      },
      userType: userProfile?.userType || 'athlete', // Asignar tipo si no lo tiene
      lastUpdated: serverTimestamp()
    };

    // Actualizar perfil del usuario
    await updateDoc(doc(db, 'users', user.uid), linkData);

    // Crear registro en la colección de vínculos
    await setDoc(doc(db, 'athlete_links', `${competitionId}_${athleteId}`), {
      userId: user.uid,
      athleteId,
      competitionId,
      athleteData,
      linkType: 'self_claim', // Reclamo propio
      status: 'confirmed',
      linkedAt: serverTimestamp()
    });

    return { success: true, type: 'self_claim' };

  } catch (error) {
    console.error('Error vinculando atleta:', error);
    throw error;
  }
};

/**
 * Solicita agregar un atleta al equipo del entrenador
 * Envía notificación al atleta para que apruebe
 */
export const requestAthleteForTeam = async (athleteId, competitionId, athleteData) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('AUTHENTICATION_REQUIRED');
    }

    const userProfile = await getCurrentUserProfile();
    
    if (!userProfile || userProfile.userType !== 'coach') {
      throw new Error('COACH_PROFILE_REQUIRED');
    }

    // Verificar si ya existe una solicitud pendiente
    const existingRequest = await checkExistingTeamRequest(athleteId, competitionId, user.uid);
    if (existingRequest) {
      throw new Error('REQUEST_ALREADY_EXISTS');
    }

    // Crear la solicitud de equipo
    const requestData = {
      athleteId,
      competitionId,
      coachId: user.uid,
      coachName: userProfile.displayName || userProfile.email,
      coachEmail: userProfile.email,
      teamName: userProfile.teamName || 'Equipo sin nombre',
      athleteData: {
        name: athleteData.fullName,
        number: athleteData.idNumber,
        team: athleteData.affiliation
      },
      status: 'pending',
      requestType: 'team_invitation',
      createdAt: serverTimestamp(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // Expira en 7 días
    };

    // Guardar la solicitud
    const requestRef = await addDoc(collection(db, 'team_requests'), requestData);

    // Crear notificación para el atleta
    await createAthleteNotification(athleteId, competitionId, {
      type: 'team_invitation',
      from: userProfile.displayName || userProfile.email,
      teamName: userProfile.teamName || 'Equipo sin nombre',
      requestId: requestRef.id,
      message: `${userProfile.displayName || userProfile.email} te invita a unirte a su equipo`
    });

    return { 
      success: true, 
      type: 'team_request',
      requestId: requestRef.id,
      message: 'Solicitud enviada. El atleta recibirá una notificación.'
    };

  } catch (error) {
    console.error('Error solicitando atleta para equipo:', error);
    throw error;
  }
};

/**
 * Verifica si ya existe una solicitud de equipo pendiente
 */
const checkExistingTeamRequest = async (athleteId, competitionId, coachId) => {
  try {
    const q = query(
      collection(db, 'team_requests'),
      where('athleteId', '==', athleteId),
      where('competitionId', '==', competitionId),
      where('coachId', '==', coachId),
      where('status', '==', 'pending')
    );

    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error verificando solicitud existente:', error);
    return false;
  }
};

/**
 * Crea una notificación para el atleta
 */
const createAthleteNotification = async (athleteId, competitionId, notificationData) => {
  try {
    // Buscar si el atleta ya está registrado en el sistema
    const q = query(
      collection(db, 'athlete_links'),
      where('athleteId', '==', athleteId),
      where('competitionId', '==', competitionId)
    );

    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      // El atleta está registrado, enviar notificación a su cuenta
      const athleteLink = snapshot.docs[0].data();
      await addDoc(collection(db, 'notifications'), {
        userId: athleteLink.userId,
        ...notificationData,
        athleteId,
        competitionId,
        read: false,
        createdAt: serverTimestamp()
      });
    } else {
      // El atleta no está registrado, guardar notificación pendiente
      await addDoc(collection(db, 'pending_notifications'), {
        athleteId,
        competitionId,
        ...notificationData,
        createdAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error creando notificación:', error);
  }
};

/**
 * Responde a una solicitud de equipo (aprobar/rechazar)
 */
export const respondToTeamRequest = async (requestId, response, athleteId, competitionId) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('AUTHENTICATION_REQUIRED');
    }

    // Obtener la solicitud
    const requestDoc = await getDoc(doc(db, 'team_requests', requestId));
    if (!requestDoc.exists()) {
      throw new Error('REQUEST_NOT_FOUND');
    }

    const requestData = requestDoc.data();

    // Verificar que el usuario actual puede responder a esta solicitud
    const userProfile = await getCurrentUserProfile();
    const isAthleteOwner = userProfile?.linkedAthlete?.athleteId === athleteId;
    
    if (!isAthleteOwner) {
      throw new Error('UNAUTHORIZED_RESPONSE');
    }

    // Actualizar el estado de la solicitud
    await updateDoc(doc(db, 'team_requests', requestId), {
      status: response, // 'approved' o 'rejected'
      respondedAt: serverTimestamp(),
      respondedBy: user.uid
    });

    if (response === 'approved') {
      // Agregar el atleta al equipo del entrenador
      await addAthleteToCoachTeam(requestData.coachId, athleteId, competitionId, requestData.athleteData);
      
      // Notificar al entrenador
      await addDoc(collection(db, 'notifications'), {
        userId: requestData.coachId,
        type: 'team_request_approved',
        athleteName: requestData.athleteData.name,
        message: `${requestData.athleteData.name} aceptó unirse a tu equipo`,
        read: false,
        createdAt: serverTimestamp()
      });
    } else {
      // Notificar al entrenador del rechazo
      await addDoc(collection(db, 'notifications'), {
        userId: requestData.coachId,
        type: 'team_request_rejected',
        athleteName: requestData.athleteData.name,
        message: `${requestData.athleteData.name} rechazó la invitación a tu equipo`,
        read: false,
        createdAt: serverTimestamp()
      });
    }

    return { success: true, response };

  } catch (error) {
    console.error('Error respondiendo solicitud:', error);
    throw error;
  }
};

/**
 * Agrega un atleta al equipo del entrenador
 */
const addAthleteToCoachTeam = async (coachId, athleteId, competitionId, athleteData) => {
  try {
    // Crear registro en la colección de equipos
    await addDoc(collection(db, 'coach_teams'), {
      coachId,
      athleteId,
      competitionId,
      athleteData,
      addedAt: serverTimestamp(),
      status: 'active'
    });

    // Actualizar el perfil del entrenador
    const coachDoc = await getDoc(doc(db, 'users', coachId));
    const coachData = coachDoc.data();
    const currentTeam = coachData?.team || [];
    
    if (!currentTeam.some(athlete => athlete.athleteId === athleteId)) {
      await updateDoc(doc(db, 'users', coachId), {
        team: [...currentTeam, {
          athleteId,
          competitionId,
          athleteData,
          addedAt: serverTimestamp()
        }],
        lastUpdated: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error agregando atleta al equipo:', error);
    throw error;
  }
};

/**
 * Obtiene las notificaciones del usuario actual
 */
export const getUserNotifications = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return [];

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error obteniendo notificaciones:', error);
    return [];
  }
};

/**
 * Determina qué tipo de acción mostrar según el usuario
 */
export const getActionTypeForUser = async (athleteId, competitionId) => {
  try {
    const userProfile = await getCurrentUserProfile();
    
    if (!userProfile) {
      return { type: 'login_required' };
    }

    // Si ya tiene este atleta vinculado
    if (userProfile.linkedAthlete?.athleteId === athleteId && 
        userProfile.linkedAthlete?.competitionId === competitionId) {
      return { type: 'already_linked' };
    }

    // Si es entrenador
    if (userProfile.userType === 'coach') {
      return { type: 'coach_request' };
    }

    // Si es atleta o usuario sin tipo definido
    return { type: 'athlete_claim' };

  } catch (error) {
    console.error('Error determinando tipo de acción:', error);
    return { type: 'error' };
  }
};